import ENUMS from '@core/classes/Enums';
import moment from 'moment';
import FIMenuCustomer from '../FIMenuCustomer';
import Application from '../Applications/Application';

export class PlaidUserInfo {
	emailAddress?: string;

	/** Format: E.164 (ex. +11234567890) */
	phoneNumber?: string;

	/** Format: YYYY-MM-DD */
	dateOfBirth?: string;

	name?: {
		givenName: string;
		familyName: string;
	};

	address?: PlaidAddress;

	idNumber?: {
		value: string;
		/** Use ENUMS.PLAID_ID_TYPES */
		type: string;
	};

	isIdEncrypted: boolean = false;

	constructor(init?: Partial<PlaidUserInfo>) {
		this.emailAddress = init?.emailAddress;
		this.phoneNumber = init?.phoneNumber;
		this.dateOfBirth = init?.dateOfBirth;
		this.name = init?.name;
		this.address = init?.address;
		this.idNumber = init?.idNumber;
		this.isIdEncrypted = init?.isIdEncrypted ?? false;
	}

	isPracticallyEmpty(): boolean {
		return !this.emailAddress && !this.phoneNumber && !this.name?.givenName && !this.dateOfBirth && !this.address?.street;
	}

	static getDefault(): PlaidUserInfo {
		return new PlaidUserInfo({
			name: { givenName: null, familyName: null },
			emailAddress: null,
			phoneNumber: null,
			dateOfBirth: null,
			idNumber: { value: null, type: null },
			address: {
				street: null,
				street2: null,
				city: null,
				region: null,
				postalCode: null,
				country: null,
			},
		});
	}

	static getSandboxUser(): PlaidUserInfo {
		return new PlaidUserInfo({
			emailAddress: 'leslies@mail.com',
			phoneNumber: '+12345678909',
			name: { givenName: 'Leslie', familyName: 'Knope' },
			dateOfBirth: '1975-01-18',
			address: {
				street: '123 Main St.',
				city: 'Pawnee',
				region: 'IN',
				postalCode: '46001',
				country: 'US',
			},
			idNumber: {
				value: '123456789',
				type: ENUMS.PLAID_ID_TYPES.UnitedStatesSSN,
			},
			isIdEncrypted: false,
		});
	}

	static getUserFromFIMenuCustomer(fimenuCustomer: FIMenuCustomer) {
		if (fimenuCustomer.isEntity) throw new Error('Cannot create a Plaid User from a business entity.');

		let dob = new Date(Date.parse(fimenuCustomer.dob)).toISOString();
		dob = dob.substring(0, dob.indexOf('T'));

		const userData = new PlaidUserInfo({
			emailAddress: fimenuCustomer.email?.trim(),
			phoneNumber: `+1${fimenuCustomer.cell}`,
			dateOfBirth: dob,
			name: {
				givenName: fimenuCustomer.firstName?.trim(),
				familyName: fimenuCustomer.lastName?.trim(),
			},
			address: {
				street: fimenuCustomer.address?.trim(),
				street2: fimenuCustomer.addressExtra?.trim(),
				city: fimenuCustomer.city?.trim(),
				region: fimenuCustomer.state?.trim(),
				postalCode: fimenuCustomer.zip?.trim(),
				country: 'US',
			},
		});

		if (fimenuCustomer.ssnHashed) {
			userData.idNumber = {
				value: fimenuCustomer.ssnHashed,
				type: ENUMS.PLAID_ID_TYPES.UnitedStatesSSN,
			};
			userData.isIdEncrypted = true;
		}

		return userData;
	}

	static getUserFromCustomerApplication(customerApplication: Application) {
		const preFilledData = customerApplication.preFilledData;
		const personalInfo = customerApplication.quoteAppData.applicationsDataAnswered.personalInfo;

		const userData = new PlaidUserInfo({
			emailAddress: preFilledData.emailAddress,
			phoneNumber: `+1${preFilledData.phoneNumber}`,
			dateOfBirth: moment(personalInfo.dateOfBirth, 'YYYY-MM-DDTHH:mm:ss.SSSSSSS[Z]').format('YYYY-MM-DD'),
			name: {
				givenName: personalInfo.firstName,
				familyName: personalInfo.lastName,
			},
			address: {
				street: personalInfo.currentResidence.addressLine1,
				street2: personalInfo.currentResidence.addressLine2,
				city: personalInfo.currentResidence.city,
				region: personalInfo.currentResidence.state,
				postalCode: personalInfo.currentResidence.zipCode,
				country: personalInfo.currentResidence.country,
			},
			idNumber: null,
			isIdEncrypted: false,
		});

		if (personalInfo.ssnHashed) {
			userData.idNumber = {
				value: personalInfo.ssnHashed,
				type: ENUMS.PLAID_ID_TYPES.UnitedStatesSSN,
			};
			userData.isIdEncrypted = true;
		}

		return userData;
	}
}

/** PlaidUserInfo objects from the backend include the clientUserId */
export class PlaidUserInfoRequest extends PlaidUserInfo {
	clientUserId?: string;

	constructor(customerId?: string, init?: Partial<PlaidUserInfo>) {
		super(init);
		this.clientUserId = customerId;
	}
}

export class PlaidAddress {
	street: string;

	street2?: string;

	city?: string;

	/** ISO 3166-2 subdivision code (ex. state, province, prefecture, zone, etc.) */
	region?: string;

	postalCode?: string;

	/** Format: ISO 3166-1 alpha-2 two-letter code */
	country?: string;

	constructor(init?: Partial<PlaidAddress>) {
		this.street = init?.street;
		this.street2 = init?.street2;
		this.city = init?.city;
		this.region = init?.region;
		this.postalCode = init?.postalCode;
		this.country = init?.country;
	}
}

export interface PlaidAccountOwner {
	/** A list of names associated with the account by the financial institution. In the case of a joint account, Plaid will make a best effort to
	 * report the names of all account holders. If an Item contains multiple accounts with different owner names, some institutions will report all
	 * names associated with the Item in each account's `names` array*/
	names: string[];

	/** A list of phone numbers associated with the account by the financial institution */
	phoneNumbers: {
		/** The phone number */
		data: string;

		/** When `true`, identifies the phone number as the primary number on the account */
		primary: boolean;

		/** The type of phone number */
		type: string;
	}[];

	/** A list of email addresses associated with the account by the financial institution */
	emails: {
		/** The email address */
		data: string;

		/** When `true`, identifies the email address as the primary email on an account */
		primary: boolean;

		/** The type of email account as described by the financial institution */
		type: string;
	}[];

	/** Data about the various addresses associated with the account by the financial institution */
	addresses: {
		/** Data about the components comprising an address */
		data: PlaidAddress;

		/** When `true`, identifies the address as the primary address on an account */
		primary: boolean;
	}[];

	/** The ID of the document that this identity belongs to */
	documentId?: string;
}
